import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { DateTime } from 'luxon'
import {
  createDemoInvitation,
  getDemoInvitations,
} from '../firestore/Invitation'
import { fetchAllAppUsersByRole } from '../firestore/PublicUser'
import {
  adminStartDemoMeeting,
  createDemoRoomState,
  deleteDemoRoomState,
  forceAIProcessing,
  getDemoRoomStates,
  getRoomWasGraded,
  roomStateAddUser,
  updateRoomState,
} from '../firestore/RoomState'
import { getDemoSlideDecks } from '../firestore/SlideDeck'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { RoomState } from '../models/RoomState'
import { SlideDeck } from '../models/SlideDeck'
import type { AppUser } from '../stores/AppUser'
import {
  RoomStateVideoMethod,
  UserProfileRole,
  type StaticModelCollection,
} from '../types'
import { Cubit } from './core'
import type { Invitation } from '../models/Invitation'
import { arrayUnion } from 'firebase/firestore'
import { pollForRoomToken } from '../util/observe'
import { fetchUserProfileFavoriteSlideDecks } from '../firestore/UserProfileFavoriteSlideDeck'
import { RoomStateDemoType } from '../firestore/RoomState/types'

export type AdminDemosSortingStrategy =
  | 'scheduledAt'
  | 'instructorName'
  | 'experience'

export type AdminDemosTab = 'upcoming' | 'active' | 'completed'

export class AdminDemosCubit extends Cubit {
  repository: FirebaseRepository

  @observable sortingStrategy: AdminDemosSortingStrategy = 'scheduledAt'
  @observable sortingOrder: 'asc' | 'desc' = 'desc'
  roomStates: StaticModelCollection<RoomState>
  slideDecks: StaticModelCollection<SlideDeck>
  favoriteSlideDeckIds = observable.array<string>()
  notPublishedButSelectedSlideDecks: StaticModelCollection<SlideDeck>
  admins = observable.array<AppUser>()
  invitations = observable.map<string, Invitation[]>()
  roomHasGradingResults = observable.map<string, boolean>()
  private roomIdFromParam: string | undefined

  constructor(
    repository: FirebaseRepository,
    roomIdFromParam: string | undefined
  ) {
    super()
    makeObservable(this)

    this.repository = repository

    this.roomStates = RoomState.emptyCollection(repository)

    this.slideDecks = SlideDeck.emptyCollection(repository)
    this.notPublishedButSelectedSlideDecks =
      SlideDeck.emptyCollection(repository)

    this.roomIdFromParam = roomIdFromParam
  }

  initialize(): void {
    this.loadDemoRoomStates()
    this.addStream(getDemoSlideDecks(this.repository), (slideDecks) => {
      this.slideDecks.replaceModels(slideDecks)
    })

    fetchAllAppUsersByRole(this.repository, [
      UserProfileRole.admin,
      UserProfileRole.corre,
    ]).then((users) => {
      if (this.isDisposed) return
      this.admins.replace(users)
    })

    // fetch favorite slide decks
    fetchUserProfileFavoriteSlideDecks(this.repository, {
      userId: this.repository.uid,
    }).then((favorites) => {
      if (this.isDisposed) return
      runInAction(() => {
        this.favoriteSlideDeckIds.replace(
          favorites.map((favorite) => favorite.data.slideDeckId)
        )
      })
    })
  }

  @action
  changeSortingStrategy(
    strategy: AdminDemosSortingStrategy,
    order: 'asc' | 'desc'
  ): void {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  isRoomGraded(roomId: string): boolean {
    return this.roomHasGradingResults.get(roomId) ?? false
  }

  loadDemoRoomStates(): void {
    if (!this.hasStream('roomStates')) {
      this.addStream(
        getDemoRoomStates(this.repository),
        (roomStates) => {
          this.roomStates.replaceModels(roomStates)
          this.startInvitationsStreams(roomStates)
          this.startGradingStreams(roomStates)
        },
        {
          name: 'roomStates',
        }
      )
    }
  }

  /** Returns the tab that should be selected based on the roomIdFromParam,
   * or undefined if the room hasn't loaded yet or there's no param. */
  @computed
  get roomFromParamTab(): AdminDemosTab | undefined {
    if (!this.roomIdFromParam) return undefined
    const room = this.roomStates.models.find(
      ({ id }) => id === this.roomIdFromParam
    )
    if (!room) return undefined
    return this.roomTab(room)
  }

  roomTab(room: RoomState): AdminDemosTab {
    if (room.isDemoCompleted) return 'completed'
    if (room.didStart) return 'active'
    // Unstarted rooms past their scheduled time are treated as completed
    const scheduledAt = room.data.scheduledAt
    if (
      scheduledAt &&
      DateTime.fromJSDate(scheduledAt).plus({ hours: 1 }) < DateTime.now()
    ) {
      return 'completed'
    }
    return 'upcoming'
  }

  getFilteredRooms(tab: AdminDemosTab): RoomState[] {
    return this.roomStates.models.filter((room) => this.roomTab(room) === tab)
  }

  @computed
  get upcomingCount(): number {
    return this.roomStates.models.filter(
      (room) => this.roomTab(room) === 'upcoming'
    ).length
  }

  @computed
  get activeCount(): number {
    return this.roomStates.models.filter(
      (room) => this.roomTab(room) === 'active'
    ).length
  }

  @computed
  get completedCount(): number {
    return this.roomStates.models.filter(
      (room) => this.roomTab(room) === 'completed'
    ).length
  }

  private getInstructorName(room: RoomState): string {
    return (
      this.admins.find((admin) => room.groupLeaderUserIds?.includes(admin.uid))
        ?.publicUser.fullName || ''
    )
  }

  private getExperienceName(room: RoomState): string {
    return (
      this.slideDecks.models.find((sd) => sd.id === room.data.slideDeckId)?.data
        .slideDeckName || ''
    )
  }

  getRooms(tab: AdminDemosTab): RoomState[] {
    const dir = this.sortingOrder === 'asc' ? 1 : -1
    return [...this.getFilteredRooms(tab)].sort((a, b) => {
      let cmp = 0
      switch (this.sortingStrategy) {
        case 'scheduledAt': {
          const dateA = a.scheduledAtDate?.toMillis() || 0
          const dateB = b.scheduledAtDate?.toMillis() || 0
          cmp = dateA - dateB
          break
        }
        case 'instructorName': {
          cmp = this.getInstructorName(a).localeCompare(
            this.getInstructorName(b)
          )
          break
        }
        case 'experience': {
          cmp = this.getExperienceName(a).localeCompare(
            this.getExperienceName(b)
          )
          break
        }
      }
      return cmp * dir
    })
  }

  startInvitationsStreams(roomStates: RoomState[]): void {
    for (const room of roomStates) {
      const name = `invitations-${room.id}`

      if (this.hasStream(name)) continue

      this.addStream(
        getDemoInvitations(this.repository, { roomId: room.id }),
        (invitations) => {
          runInAction(() => {
            invitations.sort((a, b) => {
              const [aDate, bDate] = [
                a.data.updatedAt?.getTime() ?? 0,
                b.data.updatedAt?.getTime() ?? 0,
              ]
              const aName = `${a.data.demoArguments?.firstName ?? ''} ${a.data.demoArguments?.lastName ?? ''}`
              const bName = `${b.data.demoArguments?.firstName ?? ''} ${b.data.demoArguments?.lastName ?? ''}`

              // sort by updatedAt, then name
              if (aDate !== bDate) return aDate - bDate
              return aName.localeCompare(bName)
            })

            this.invitations.set(room.id, invitations)
          })
        },
        {
          name,
        }
      )
    }
  }

  private startGradingStreams(roomStates: RoomState[]): void {
    for (const room of roomStates) {
      if (!room.didStart) continue
      const name = `roomGraded-${room.id}`
      if (this.hasStream(name)) continue
      this.addStream(
        getRoomWasGraded(this.repository, { roomStateId: room.id }),
        (graded) => {
          this.roomHasGradingResults.set(room.id, graded)
        },
        { name }
      )
    }
  }

  async deleteRoomState(roomStateId: string) {
    await deleteDemoRoomState(this.repository, {
      roomStateId,
    })
  }

  async createDemoMeeting({
    userId,
    slideDeckId,
    scheduledAt,
    demoType,
    roomStateName,
  }: {
    userId: string
    slideDeckId: string
    scheduledAt: DateTime
    demoType?: RoomStateDemoType
    roomStateName?: string
  }) {
    const result = await createDemoRoomState(this.repository, {
      userId: demoType === RoomStateDemoType.solo ? undefined : userId,
      slideDeckId,
      scheduledAt,
      demoType,
      name: roomStateName,
    })

    return result.id
  }

  async updateDemoMeeting(
    roomStateId: string,
    {
      userId,
      slideDeckId,
      scheduledAt,
      demoType,
      roomStateName,
      previousUserId,
    }: {
      userId: string
      slideDeckId: string
      scheduledAt: DateTime
      demoType?: RoomStateDemoType
      roomStateName?: string
      previousUserId?: string
    }
  ) {
    if (demoType === RoomStateDemoType.solo) {
      await updateRoomState(this.repository.firestore, roomStateId, {
        userIds: [],
        groupLeaderUserIds: [],
        slideDeckId,
        demoType: RoomStateDemoType.solo,
        scheduledAt: scheduledAt.toJSDate(),
        roomStateName: roomStateName ?? '',
      })
    } else {
      let userIds: string[] | ReturnType<typeof arrayUnion> = arrayUnion(userId)
      if (previousUserId && previousUserId !== userId) {
        // Compute new userIds in a single update to avoid intermediate empty state
        // that causes the demo to disappear from the real-time listener
        const currentRoom = this.roomStates.models.find(
          (r) => r.id === roomStateId
        )
        const currentUserIds = currentRoom?.data.userIds ?? []
        const newUserIds = currentUserIds.filter((id) => id !== previousUserId)
        if (!newUserIds.includes(userId)) newUserIds.push(userId)
        userIds = newUserIds
      }

      await updateRoomState(this.repository.firestore, roomStateId, {
        userIds,
        groupLeaderUserIds: [userId],
        slideDeckId,
        demoType: RoomStateDemoType.regular,
        scheduledAt: scheduledAt.toJSDate(),
        roomStateName: roomStateName ?? '',
      })
    }
  }

  inviteToDemo(
    roomId: string,
    demoType: RoomStateDemoType,
    {
      firstName,
      lastName,
      emailAddress,
    }: {
      firstName: string
      lastName: string
      emailAddress: string
    }
  ) {
    return createDemoInvitation(this.repository, {
      roomId,
      firstName,
      lastName,
      emailAddress,
      demoType,
    })
  }

  /**
   * adds user to room and checks for the token on user profile
   * resolves to true if the token is present
   */
  async joinMeeting(roomId: string) {
    await roomStateAddUser(this.repository.firestore, {
      roomId,
      userId: this.repository.uid,
      isGroupLeader: false,
    })
    return await pollForRoomToken(this.repository, roomId)
  }

  /**
   * starts room and checks for the token on user profile
   * resolves to true if the token is present
   */
  async startMeeting(room: RoomState) {
    await adminStartDemoMeeting(this.repository, { roomStateId: room.id })
    return await pollForRoomToken(this.repository, room.id)
  }

  getRoomRowData(room: RoomState) {
    const groupLeaderIds = room.data.groupLeaderUserIds
    const isSoloDemo = room.data.demoType === RoomStateDemoType.solo
    const students = this.repository.userStore
      .getUsers(room.data.userIds)
      .filter(
        (user) =>
          (isSoloDemo || !groupLeaderIds.includes(user.id)) &&
          user.fullName.trim()
      )
    const instructor = groupLeaderIds[0]
      ? this.repository.userStore.getUser(groupLeaderIds[0])
      : null
    const slideDeck = this.slideDecks.models.find(
      (sd) => sd.id === room.data.slideDeckId
    )
    return { students, instructor, slideDeck, isSoloDemo }
  }

  forceAIProcessing = (roomId: string) =>
    forceAIProcessing(this.repository, { roomId })

  @action
  ensureVideoMethod(roomId: string, expectedMethod: RoomStateVideoMethod) {
    const roomState = this.roomStates.models.find(
      (roomState) => roomState.id === roomId
    )
    if (!roomState) return Promise.resolve()
    if (roomState.data?.videoMethod === expectedMethod) return Promise.resolve()

    return updateRoomState(this.repository.firestore, roomId, {
      videoMethod: expectedMethod,
    })
  }
}
